import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SendIcon from '@mui/icons-material/Send';
import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import GroupsIcon from '@mui/icons-material/Groups';
import ExtensionIcon from '@mui/icons-material/Extension';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ContactsIcon from '@mui/icons-material/Contacts';
import SecurityIcon from '@mui/icons-material/Security';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import BusinessIcon from '@mui/icons-material/Business';
import PaletteIcon from '@mui/icons-material/Palette';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SavingsOutlinedIcon from '@mui/icons-material/SavingsOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dashboardPathForTab } from '../../lib/dashboardRoutes';
import {
  DashboardModuleRoot,
  DashboardModuleToolbar,
  DashboardModuleBody,
} from '../layout/DashboardLayout';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';
import { appendTicketLog } from '../../lib/ticketLogs';
import CompanyTimezoneSettings from './CompanyTimezoneSettings';
import CompanyGeneralSettings from './CompanyGeneralSettings';
import WorkspaceBrandingSettings from './WorkspaceBrandingSettings';
import CrmApiAccess from '../crm/CrmApiAccess';
import WebhookManager from './WebhookManager';
import AiWorkforceRosterPanel from './AiWorkforceRosterPanel';
import MultiProviderConfig from './MultiProviderConfig';
import AiSpendBudgetSettings from './AiSpendBudgetSettings';
import AiSubscriptionsSettings from './AiSubscriptionsSettings';
import { createModuleRequest, MODULE_REQUEST_STATUS } from '../../lib/moduleRequests';
import { formatApiKeyDate, generateShiftyApiKey, maskApiKey } from '../../lib/apiKeys';
import {
  ACTIVE_MODULE_KEYS,
  ACTIVE_MODULE_DESCRIPTIONS,
  ACTIVE_MODULE_LABELS,
  normalizeActiveModules,
} from '../../lib/companyActiveModules';
import {
  hasRolePermission,
  MASTER_MODULE_KEYS,
  MASTER_MODULE_LABELS,
  normalizeRolePermissions,
  PERMISSION_MANAGE_ROLES,
  PERMISSION_ROLE_LABELS,
  resolveMasterActiveModules,
  toggleRolePermission,
} from '../../lib/rolePermissions';

/** Which internal roles can use each hub module when it is enabled for the tenant. */
const MODULE_ROLE_ACCESS = {
  ticketing: ['Company Admin', 'Developer', 'Manager', 'Agent', 'Trainee'],
  agileKanban: ['Company Admin', 'Developer', 'Manager'],
  uptimeMonitoring: ['Company Admin', 'Developer'],
  portfolio: ['Company Admin'],
  vault: ['Company Admin', 'Developer'],
  codeReview: ['Company Admin', 'Developer'],
  chatWidget: ['Company Admin'],
};

const INTERNAL_ROLES = [
  {
    role: 'Developer',
    summary: 'Engineering access — monitoring, agile boards, and technical modules when enabled.',
  },
  {
    role: 'Manager',
    summary: 'Operations lead — tickets, sprint planning, and team activity when modules are on.',
  },
  {
    role: 'Agent',
    summary: 'Frontline support — assigned tickets and workspace tools tied to ticketing.',
  },
  {
    role: 'Trainee',
    summary: 'Limited agent access — primarily ticket workflows when ticketing is enabled.',
  },
];

/** Nested subsections under Settings › Workspace. */
const WORKSPACE_SECTIONS = [
  {
    id: 'general',
    label: 'General settings',
    description:
      'Company profile, ticketing preferences, and global support widget triage.',
    icon: BusinessIcon,
  },
  {
    id: 'branding',
    label: 'Workspace branding',
    description: 'Logo, brand color, and portal background.',
    icon: PaletteIcon,
  },
  {
    id: 'modules',
    label: 'Active modules',
    description: 'Modules enabled for your workspace and upgrade requests.',
    icon: ExtensionIcon,
  },
  {
    id: 'roles',
    label: 'Role management',
    description: 'Role access overview and Granular Role Permissions.',
    icon: GroupsIcon,
  },
  {
    id: 'integrations',
    label: 'API & integrations',
    description: 'AI providers, API keys, webhooks, and CRM lead ingestion.',
    icon: VpnKeyIcon,
  },
  {
    id: 'ai-spend',
    label: 'AI Spend',
    description: 'Daily, weekly, and monthly AI budgets plus forward planning.',
    icon: SavingsOutlinedIcon,
  },
  {
    id: 'ai-roster',
    label: 'AI Workforce Roster',
    description: 'Unified AI Studio agents — models, prompts, and code review.',
    icon: SmartToyOutlinedIcon,
  },
  {
    id: 'regional',
    label: 'Regional',
    description: 'Display timezone for this workspace.',
    icon: AccessTimeIcon,
  },
];

/** Related settings that live in their own top-level tabs. */
const RELATED_SETTINGS = [
  {
    tab: 'Users',
    label: 'Users & access',
    description: 'Invite teammates and assign internal roles.',
    icon: GroupsIcon,
  },
  {
    tab: 'Notifications',
    label: 'Notifications inbox',
    description: 'Your workspace alerts and updates.',
    icon: NotificationsIcon,
  },
  {
    tab: 'Profile',
    label: 'My profile',
    description: 'Your personal account and preferences.',
    icon: AccountCircleIcon,
  },
  {
    tab: 'SettingsBilling',
    label: 'Billing & plan',
    description: 'Subscription, plan, and invoices.',
    icon: CreditCardOutlinedIcon,
    disabled: true,
    hint: 'Coming soon',
  },
];

/** Supported BYOK outbound email providers (UI facade for the MVP). */
const EMAIL_PROVIDERS = [
  { id: 'sendgrid', label: 'SendGrid' },
  { id: 'resend', label: 'Resend' },
  { id: 'mailgun', label: 'Mailgun' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Reserved config id for the company-wide fallback provider. */
const COMPANY_DEFAULT_CONFIG_ID = '__company_default__';

/** BYO storage providers for chat attachments (UI facade). */
const STORAGE_PROVIDERS = [
  { id: 'shiftypm_default', label: 'ShiftyPM Default' },
  { id: 'aws_s3', label: 'AWS S3 Bucket' },
  { id: 'google_drive', label: 'Google Drive Service Account' },
];

const STORAGE_DEFAULT_CONFIG_ID = 'default';
const GITHUB_INTEGRATION_DOC_ID = 'github';
const STABILITY_INTEGRATION_DOC_ID = 'stability';

/** @returns {{ target: string, token: string, tokenConfigured: boolean, tokenKind: string }} */
function emptyGithubConnection() {
  return { target: '', token: '', tokenConfigured: false, tokenKind: '' };
}

/**
 * @param {string | null | undefined} token
 * @returns {string}
 */
function detectGithubTokenKind(token) {
  const value = typeof token === 'string' ? token.trim() : '';
  if (value.startsWith('github_pat_')) return 'fine_grained';
  if (value.startsWith('ghp_')) return 'classic';
  if (value) return 'other';
  return '';
}

/**
 * Mask a stored GitHub PAT for display (never re-show the secret).
 * @param {string | null | undefined} token
 * @returns {string}
 */
function maskGithubPat(token) {
  const value = typeof token === 'string' ? token.trim() : '';
  if (!value) return '';
  if (value.startsWith('github_pat_')) return 'github_pat_************************';
  if (value.startsWith('ghp_')) return 'ghp_************************';
  return `${value.slice(0, 4)}${'*'.repeat(24)}`;
}

/**
 * Normalize stored GitHub integration into connection rows (incl. legacy).
 * Secrets are never kept in editable form state — only a mask + kind.
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {Array<{ target: string, token: string, tokenConfigured: boolean, tokenKind: string }>}
 */
function normalizeGithubConnectionsFromDoc(data) {
  const source = data && typeof data === 'object' ? data : {};
  const toRow = (target, token) => {
    const trimmed = typeof token === 'string' ? token.trim() : '';
    const configured = Boolean(trimmed);
    return {
      target: typeof target === 'string' ? target : '',
      token: '',
      tokenConfigured: configured,
      tokenKind: detectGithubTokenKind(trimmed),
    };
  };

  if (Array.isArray(source.githubConnections) && source.githubConnections.length > 0) {
    return source.githubConnections.map((row) => {
      if (!row || typeof row !== 'object') return emptyGithubConnection();
      const target =
        typeof row.target === 'string'
          ? row.target
          : typeof row.org === 'string'
            ? row.org
            : typeof row.orgOrUsername === 'string'
              ? row.orgOrUsername
              : '';
      const token =
        typeof row.token === 'string'
          ? row.token
          : typeof row.personalAccessToken === 'string'
            ? row.personalAccessToken
            : '';
      return toRow(target, token);
    });
  }
  const legacyToken =
    typeof source.personalAccessToken === 'string' ? source.personalAccessToken : '';
  const legacyOrg =
    typeof source.orgOrUsername === 'string' ? source.orgOrUsername : '';
  if (legacyToken || legacyOrg) {
    return [toRow(legacyOrg, legacyToken)];
  }
  return [emptyGithubConnection()];
}

/**
 * Full tokens from Firestore (server-side merge only — not for UI state).
 * @param {Record<string, unknown> | null | undefined} data
 * @returns {Array<{ target: string, token: string }>}
 */
function extractGithubConnectionSecrets(data) {
  const source = data && typeof data === 'object' ? data : {};
  if (Array.isArray(source.githubConnections) && source.githubConnections.length > 0) {
    return source.githubConnections
      .map((row) => {
        if (!row || typeof row !== 'object') return null;
        const target =
          typeof row.target === 'string'
            ? row.target.trim()
            : typeof row.org === 'string'
              ? row.org.trim()
              : typeof row.orgOrUsername === 'string'
                ? row.orgOrUsername.trim()
                : '';
        const token =
          typeof row.token === 'string'
            ? row.token.trim()
            : typeof row.personalAccessToken === 'string'
              ? row.personalAccessToken.trim()
              : '';
        if (!target && !token) return null;
        return { target, token };
      })
      .filter(Boolean);
  }
  const legacyToken =
    typeof source.personalAccessToken === 'string'
      ? source.personalAccessToken.trim()
      : '';
  const legacyOrg =
    typeof source.orgOrUsername === 'string' ? source.orgOrUsername.trim() : '';
  if (legacyToken || legacyOrg) {
    return [{ target: legacyOrg, token: legacyToken }];
  }
  return [];
}

/**
 * Mask a stored API key for display (e.g. ••••••••1234).
 * @param {string | null | undefined} key
 * @returns {string}
 */
function maskByokApiKey(key) {
  if (!key || typeof key !== 'string') return '';
  const trimmed = key.trim();
  if (!trimmed) return '';
  if (trimmed.length <= 4) return '••••';
  return `${'•'.repeat(8)}${trimmed.slice(-4)}`;
}

/**
 * @param {string} input
 * @param {string} storedFull
 * @returns {boolean}
 */
function isMaskedByokPlaceholder(input, storedFull) {
  if (!input || !storedFull) return false;
  return input === maskByokApiKey(storedFull) || /^•+\S{0,4}$/.test(input.trim());
}

/**
 * Company Admin view of tenant modules and internal role capabilities.
 * @param {{ userData?: { companyId?: string, role?: string, email?: string } }} props
 */
export default function WorkspaceSettings({ userData }) {
  const { workspace, loading: workspaceLoading, companyId } = useWorkspace();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const isCompanyAdmin = userData?.role === 'company_admin';
  const resolvedCompanyId = companyId || userData?.companyId;

  const [requestingKey, setRequestingKey] = useState('');
  const [requestMsg, setRequestMsg] = useState('');
  const [requestError, setRequestError] = useState('');

  const [generateOpen, setGenerateOpen] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [keyNameError, setKeyNameError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [apiKeyError, setApiKeyError] = useState('');
  const [apiKeySuccess, setApiKeySuccess] = useState('');
  const [revealedKey, setRevealedKey] = useState(null);
  const [revokingId, setRevokingId] = useState('');
  const [copiedId, setCopiedId] = useState('');

  const [rolePermissions, setRolePermissions] = useState(() => normalizeRolePermissions(null));
  const [permissionsDirty, setPermissionsDirty] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [permissionsMsg, setPermissionsMsg] = useState('');
  const [permissionsError, setPermissionsError] = useState('');

  const [emailConfigTarget, setEmailConfigTarget] = useState(COMPANY_DEFAULT_CONFIG_ID);
  const [emailConfigs, setEmailConfigs] = useState({});
  const [crmApps, setCrmApps] = useState([]);
  const [emailProvider, setEmailProvider] = useState('sendgrid');
  const [emailApiKey, setEmailApiKey] = useState('');
  const [emailSender, setEmailSender] = useState('');
  const [showEmailKey, setShowEmailKey] = useState(false);
  const [emailConfigLoading, setEmailConfigLoading] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailConfigMsg, setEmailConfigMsg] = useState('');
  const [emailConfigError, setEmailConfigError] = useState('');

  const [storageProvider, setStorageProvider] = useState('shiftypm_default');
  const [storageBucket, setStorageBucket] = useState('');
  const [storageCredentials, setStorageCredentials] = useState('');
  const [showStorageCredentials, setShowStorageCredentials] = useState(false);
  const [storageConfigLoading, setStorageConfigLoading] = useState(false);
  const [storageSaving, setStorageSaving] = useState(false);
  const [storageConfigMsg, setStorageConfigMsg] = useState('');
  const [storageConfigError, setStorageConfigError] = useState('');

  const [githubConnections, setGithubConnections] = useState(() => [
    emptyGithubConnection(),
  ]);
  const [showGithubPat, setShowGithubPat] = useState(false);
  const [githubConfigLoading, setGithubConfigLoading] = useState(false);
  const [githubSaving, setGithubSaving] = useState(false);
  const [githubConfigMsg, setGithubConfigMsg] = useState('');
  const [githubConfigError, setGithubConfigError] = useState('');
  const [githubConfigured, setGithubConfigured] = useState(false);

  const [stabilityKey, setStabilityKey] = useState('');
  const [stabilityKeyStored, setStabilityKeyStored] = useState('');
  const [showStabilityKey, setShowStabilityKey] = useState(false);
  const [stabilityConfigLoading, setStabilityConfigLoading] = useState(false);
  const [stabilitySaving, setStabilitySaving] = useState(false);
  const [stabilityConfigMsg, setStabilityConfigMsg] = useState('');
  const [stabilityConfigError, setStabilityConfigError] = useState('');
  const [stabilityConfigured, setStabilityConfigured] = useState(false);

  useEffect(() => {
    setRolePermissions(normalizeRolePermissions(workspace?.rolePermissions));
    setPermissionsDirty(false);
  }, [workspace?.rolePermissions, workspace?.id]);

  useEffect(() => {
    if (!resolvedCompanyId || !isCompanyAdmin) return undefined;
    let active = true;
    setEmailConfigLoading(true);
    Promise.all([
      getDocs(collection(db, 'companies', resolvedCompanyId, 'email_config')),
      getDocs(query(collection(db, 'apps'), where('companyId', '==', resolvedCompanyId))),
    ])
      .then(([cfgSnap, appsSnap]) => {
        if (!active) return;
        const cfgMap = {};
        cfgSnap.docs.forEach((docSnap) => {
          cfgMap[docSnap.id] = { id: docSnap.id, ...docSnap.data() };
        });
        setEmailConfigs(cfgMap);

        const appRows = appsSnap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            appName: docSnap.data()?.appName || 'Untitled app',
          }))
          .sort((a, b) => a.appName.localeCompare(b.appName));
        setCrmApps(appRows);

        // Prefill from the company master (the default selected target).
        const initial = cfgMap[COMPANY_DEFAULT_CONFIG_ID];
        if (initial) {
          setEmailProvider(
            typeof initial.provider === 'string' && initial.provider ? initial.provider : 'sendgrid',
          );
          setEmailApiKey(typeof initial.apiKey === 'string' ? initial.apiKey : '');
          setEmailSender(typeof initial.senderEmail === 'string' ? initial.senderEmail : '');
        }
      })
      .catch((e) => {
        if (active) {
          setEmailConfigError(e.message || 'Could not load email provider settings.');
        }
      })
      .finally(() => {
        if (active) setEmailConfigLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedCompanyId, isCompanyAdmin]);

  useEffect(() => {
    if (!resolvedCompanyId || !isCompanyAdmin) return undefined;
    let active = true;
    setStorageConfigLoading(true);
    getDoc(doc(db, 'companies', resolvedCompanyId, 'storage_config', STORAGE_DEFAULT_CONFIG_ID))
      .then((snap) => {
        if (!active) return;
        if (snap.exists()) {
          const data = snap.data() || {};
          setStorageProvider(
            typeof data.provider === 'string' && data.provider ? data.provider : 'shiftypm_default',
          );
          setStorageBucket(
            typeof data.bucketOrFolder === 'string' ? data.bucketOrFolder : '',
          );
          setStorageCredentials(
            typeof data.credentials === 'string' ? data.credentials : '',
          );
        }
      })
      .catch((e) => {
        if (active) {
          setStorageConfigError(e.message || 'Could not load storage settings.');
        }
      })
      .finally(() => {
        if (active) setStorageConfigLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedCompanyId, isCompanyAdmin]);

  useEffect(() => {
    if (!resolvedCompanyId || !isCompanyAdmin) return undefined;
    let active = true;
    setGithubConfigLoading(true);
    getDoc(
      doc(db, 'companies', resolvedCompanyId, 'integrations', GITHUB_INTEGRATION_DOC_ID),
    )
      .then((snap) => {
        if (!active) return;
        if (snap.exists()) {
          const data = snap.data() || {};
          const rows = normalizeGithubConnectionsFromDoc(data);
          setGithubConnections(rows);
          setGithubConfigured(rows.some((r) => r.tokenConfigured));
        } else {
          setGithubConnections([emptyGithubConnection()]);
          setGithubConfigured(false);
        }
      })
      .catch((e) => {
        if (active) {
          setGithubConfigError(e.message || 'Could not load GitHub integration settings.');
        }
      })
      .finally(() => {
        if (active) setGithubConfigLoading(false);
      });
    return () => {
      active = false;
    };
  }, [resolvedCompanyId, isCompanyAdmin]);

  useEffect(() => {
    if (!resolvedCompanyId || !isCompanyAdmin) return undefined;
    let active = true;

    const loadByokDoc = async (docId, apply) => {
      try {
        const snap = await getDoc(
          doc(db, 'companies', resolvedCompanyId, 'integrations', docId),
        );
        if (!active) return;
        // Missing doc is a normal "not configured yet" state — not an error.
        if (!snap.exists()) {
          apply({ key: '', configured: false });
          return;
        }
        const data = snap.data() || {};
        const key = typeof data.apiKey === 'string' ? data.apiKey.trim() : '';
        apply({ key, configured: Boolean(key) });
      } catch (e) {
        if (!active) return;
        console.error(`load integrations/${docId}:`, e);
        apply({
          key: '',
          configured: false,
          error: e.message || `Could not load ${docId} integration settings.`,
        });
      }
    };

    setStabilityConfigLoading(true);
    setStabilityConfigError('');

    loadByokDoc(STABILITY_INTEGRATION_DOC_ID, ({ key, configured, error }) => {
      if (!active) return;
      setStabilityKeyStored(key);
      setStabilityKey(key ? maskByokApiKey(key) : '');
      setStabilityConfigured(configured);
      if (error) setStabilityConfigError(error);
      setStabilityConfigLoading(false);
    });

    return () => {
      active = false;
    };
  }, [resolvedCompanyId, isCompanyAdmin]);

  const masterActiveModules = useMemo(
    () => resolveMasterActiveModules(workspace),
    [workspace],
  );

  const activeMasterModuleKeys = useMemo(
    () => MASTER_MODULE_KEYS.filter((key) => masterActiveModules[key]),
    [masterActiveModules],
  );

  const apiKeysQuery = useMemo(() => {
    if (!resolvedCompanyId || !isCompanyAdmin) return null;
    return query(collection(db, 'apiKeys'), where('companyId', '==', resolvedCompanyId));
  }, [resolvedCompanyId, isCompanyAdmin]);

  const { data: apiKeysRaw, loading: apiKeysLoading } = useFirestoreQuery(apiKeysQuery, {
    enabled: Boolean(resolvedCompanyId && isCompanyAdmin),
  });

  const activeApiKeys = useMemo(() => {
    const rows = apiKeysRaw.filter((k) => k.isActive !== false);
    rows.sort((a, b) => {
      const ad = a.createdAt?.toDate?.() ? a.createdAt.toDate().getTime() : 0;
      const bd = b.createdAt?.toDate?.() ? b.createdAt.toDate().getTime() : 0;
      return bd - ad;
    });
    return rows;
  }, [apiKeysRaw]);

  const modules = useMemo(
    () => normalizeActiveModules(workspace?.activeModules),
    [workspace?.activeModules],
  );

  const enabledModules = useMemo(
    () => ACTIVE_MODULE_KEYS.filter((k) => modules[k]),
    [modules],
  );
  const lockedModules = useMemo(
    () => ACTIVE_MODULE_KEYS.filter((k) => !modules[k]),
    [modules],
  );

  const moduleRequestsQuery = useMemo(() => {
    if (!resolvedCompanyId || !isCompanyAdmin) return null;
    return query(
      collection(db, 'moduleRequests'),
      where('companyId', '==', resolvedCompanyId),
      where('status', '==', MODULE_REQUEST_STATUS.PENDING),
    );
  }, [resolvedCompanyId, isCompanyAdmin]);

  const { data: pendingModuleRequests } = useFirestoreQuery(moduleRequestsQuery, {
    enabled: Boolean(resolvedCompanyId && isCompanyAdmin),
  });

  const pendingModuleKeys = useMemo(
    () => new Set(pendingModuleRequests.map((r) => r.moduleKey).filter(Boolean)),
    [pendingModuleRequests],
  );

  const handleRequestAccess = async (moduleKey) => {
    const label = ACTIVE_MODULE_LABELS[moduleKey] || moduleKey;
    const cid = companyId || userData?.companyId;
    const actor = auth.currentUser;
    if (!cid || !actor) return;

    setRequestingKey(moduleKey);
    setRequestError('');
    setRequestMsg('');
    try {
      const { requestId, ticketId } = await createModuleRequest(db, {
        companyId: cid,
        moduleKey,
        moduleLabel: label,
        companyName: workspace?.name || '',
        requestedByUid: actor.uid,
        requestedByEmail: actor.email || userData?.email || '',
        requestedByRole: userData?.role || 'company_admin',
      });
      await appendTicketLog(db, {
        companyId: cid,
        ticketId,
        action: 'module_access_requested',
        summary: `Module access requested: ${label}`,
        meta: {
          moduleKey,
          moduleLabel: label,
          companyName: workspace?.name || '',
          moduleRequestId: requestId,
          ticketId,
        },
        actorUid: actor.uid,
        actorEmail: actor.email || userData?.email || '',
        actorRole: userData?.role || 'company_admin',
        participantUids: [actor.uid],
        resourceType: 'workspace',
      });
      setRequestMsg(`Request sent for ${label}. A tracking ticket was created for your workspace owner.`);
    } catch (e) {
      console.error('WorkspaceSettings request access:', e);
      setRequestError(e.message || 'Could not submit your request.');
    } finally {
      setRequestingKey('');
    }
  };

  const openGenerateDialog = () => {
    setKeyName('');
    setKeyNameError('');
    setGenerateOpen(true);
  };

  const handleGenerateApiKey = async () => {
    const name = keyName.trim();
    if (!name) {
      setKeyNameError('Enter a name for this key (e.g. Main Website).');
      return;
    }
    if (!resolvedCompanyId) return;

    setGenerating(true);
    setApiKeyError('');
    setApiKeySuccess('');
    const key = generateShiftyApiKey();
    try {
      const docRef = await addDoc(collection(db, 'apiKeys'), {
        key,
        companyId: resolvedCompanyId,
        name,
        createdAt: serverTimestamp(),
        isActive: true,
        createdByUid: auth.currentUser?.uid || null,
      });
      setRevealedKey({ id: docRef.id, key, name });
      setApiKeySuccess(`API key "${name}" created. Copy it now — it will be masked in the list.`);
      setGenerateOpen(false);
      setKeyName('');
    } catch (e) {
      console.error('generate api key:', e);
      setApiKeyError(e.message || 'Could not generate API key.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyKey = async (row) => {
    const fullKey = row.id === revealedKey?.id ? revealedKey.key : row.key;
    if (!fullKey) return;
    try {
      await navigator.clipboard.writeText(fullKey);
      setCopiedId(row.id);
      setTimeout(() => setCopiedId(''), 2000);
    } catch (e) {
      console.error('copy api key:', e);
      setApiKeyError('Could not copy to clipboard.');
    }
  };

  const handlePermissionToggle = (role, module, action, checked) => {
    setRolePermissions((prev) => toggleRolePermission(prev, role, module, action, checked));
    setPermissionsDirty(true);
    setPermissionsMsg('');
    setPermissionsError('');
  };

  const handleSavePermissions = async () => {
    if (!resolvedCompanyId) return;
    setSavingPermissions(true);
    setPermissionsError('');
    setPermissionsMsg('');
    try {
      await updateDoc(doc(db, 'companies', resolvedCompanyId), {
        rolePermissions: normalizeRolePermissions(rolePermissions),
      });
      setPermissionsDirty(false);
      setPermissionsMsg('Role permissions saved.');
    } catch (e) {
      console.error('WorkspaceSettings save permissions:', e);
      setPermissionsError(e.message || 'Could not save role permissions.');
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleRevokeKey = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(`Revoke API key "${row.name || 'Unnamed'}"? External apps using it will stop working.`)) {
      return;
    }
    setRevokingId(row.id);
    setApiKeyError('');
    try {
      await updateDoc(doc(db, 'apiKeys', row.id), { isActive: false });
      if (revealedKey?.id === row.id) setRevealedKey(null);
      setApiKeySuccess(`API key "${row.name || 'Unnamed'}" revoked.`);
    } catch (e) {
      console.error('revoke api key:', e);
      setApiKeyError(e.message || 'Could not revoke API key.');
    } finally {
      setRevokingId('');
    }
  };

  const handleSelectEmailTarget = (target) => {
    setEmailConfigTarget(target);
    setEmailConfigMsg('');
    setEmailConfigError('');
    const cfg = emailConfigs[target];
    setEmailProvider(
      cfg && typeof cfg.provider === 'string' && cfg.provider ? cfg.provider : 'sendgrid',
    );
    setEmailApiKey(cfg && typeof cfg.apiKey === 'string' ? cfg.apiKey : '');
    setEmailSender(cfg && typeof cfg.senderEmail === 'string' ? cfg.senderEmail : '');
  };

  const handleSaveEmailConfig = async () => {
    if (!resolvedCompanyId) return;
    const target = emailConfigTarget;
    const key = emailApiKey.trim();
    const sender = emailSender.trim();
    setEmailConfigError('');
    setEmailConfigMsg('');
    if (!target) {
      setEmailConfigError('Select which app this configuration applies to.');
      return;
    }
    if (!key) {
      setEmailConfigError('Enter your provider API key.');
      return;
    }
    if (!EMAIL_RE.test(sender)) {
      setEmailConfigError('Enter a valid verified sender email (e.g. hello@yourcompany.com).');
      return;
    }
    setEmailSaving(true);
    try {
      const isDefault = target === COMPANY_DEFAULT_CONFIG_ID;
      const appName = isDefault
        ? null
        : crmApps.find((a) => a.id === target)?.appName || null;
      const payload = {
        provider: emailProvider,
        apiKey: key,
        senderEmail: sender,
        isCompanyDefault: isDefault,
        appId: isDefault ? null : target,
        appName,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      };
      await setDoc(
        doc(db, 'companies', resolvedCompanyId, 'email_config', target),
        payload,
        { merge: true },
      );
      setEmailConfigs((prev) => ({ ...prev, [target]: { id: target, ...payload } }));
      setEmailConfigMsg(
        isDefault
          ? 'Company master (fallback) email provider saved.'
          : `Email provider saved for “${appName || target}”.`,
      );
    } catch (e) {
      console.error('save email config:', e);
      setEmailConfigError(e.message || 'Could not save email configuration.');
    } finally {
      setEmailSaving(false);
    }
  };

  const handleSaveStorageConfig = async () => {
    if (!resolvedCompanyId) return;
    const bucket = storageBucket.trim();
    const credentials = storageCredentials.trim();
    const needsExternalConfig =
      storageProvider === 'aws_s3' || storageProvider === 'google_drive';

    setStorageConfigError('');
    setStorageConfigMsg('');

    if (needsExternalConfig && !bucket) {
      setStorageConfigError('Enter a bucket name or folder ID for the selected provider.');
      return;
    }
    if (needsExternalConfig && !credentials) {
      setStorageConfigError('Enter credentials JSON or secret key for the selected provider.');
      return;
    }

    setStorageSaving(true);
    try {
      const payload = {
        provider: storageProvider,
        bucketOrFolder: bucket || null,
        credentials: credentials || null,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      };
      await setDoc(
        doc(db, 'companies', resolvedCompanyId, 'storage_config', STORAGE_DEFAULT_CONFIG_ID),
        payload,
        { merge: true },
      );
      setStorageConfigMsg('File storage warehouse configuration saved.');
    } catch (e) {
      console.error('save storage config:', e);
      setStorageConfigError(e.message || 'Could not save storage configuration.');
    } finally {
      setStorageSaving(false);
    }
  };

  const handleSaveGithubConfig = async () => {
    if (!resolvedCompanyId) return;

    setGithubConfigError('');
    setGithubConfigMsg('');

    const integrationRef = doc(
      db,
      'companies',
      resolvedCompanyId,
      'integrations',
      GITHUB_INTEGRATION_DOC_ID,
    );

    let existingSecrets = [];
    try {
      const existingSnap = await getDoc(integrationRef);
      if (existingSnap.exists()) {
        existingSecrets = extractGithubConnectionSecrets(existingSnap.data() || {});
      }
    } catch (e) {
      console.error('load github secrets for merge:', e);
    }

    const cleaned = githubConnections
      .map((row, index) => {
        const target = typeof row.target === 'string' ? row.target.trim() : '';
        const typed = typeof row.token === 'string' ? row.token.trim() : '';
        let token = typed;
        if (!token && row.tokenConfigured) {
          const byIndex = existingSecrets[index];
          const byTarget = existingSecrets.find(
            (s) => s.target && s.target === target && s.token,
          );
          token = (byIndex && byIndex.token) || (byTarget && byTarget.token) || '';
        }
        return { target, token };
      })
      .filter((row) => row.target || row.token);

    if (cleaned.length === 0 || !cleaned.some((row) => row.token)) {
      setGithubConfigError(
        'Add at least one GitHub connection with a classic Personal Access Token (ghp_…).',
      );
      return;
    }

    const missingToken = cleaned.find((row) => row.target && !row.token);
    if (missingToken) {
      setGithubConfigError(
        `Enter a PAT for target “${missingToken.target}”, or revoke and paste a new classic token.`,
      );
      return;
    }

    const fineGrained = cleaned.find((row) =>
      row.token.startsWith('github_pat_') && !row.target.includes('/'),
    );
    if (fineGrained) {
      setGithubConfigError(
        'Owner-level fine-grained tokens (github_pat_…) cannot create repositories. ' +
          'Use a classic PAT (ghp_…) for targets like “tatotron”. Repo-scoped fine-grained tokens are OK for existing owner/repo entries.',
      );
      return;
    }

    const nonClassicOwner = cleaned.find(
      (row) =>
        row.token &&
        !row.token.startsWith('ghp_') &&
        !row.token.startsWith('github_pat_') &&
        !row.target.includes('/'),
    );
    if (nonClassicOwner) {
      setGithubConfigError(
        'Owner-level scaffolding tokens must be classic PATs starting with ghp_.',
      );
      return;
    }

    const hasClassic = cleaned.some((row) => row.token.startsWith('ghp_'));
    if (!hasClassic) {
      setGithubConfigError(
        'Add at least one classic PAT (ghp_…) — required to create new game repositories.',
      );
      return;
    }

    setGithubSaving(true);
    try {
      const primary = cleaned[0];
      const primaryOwner = (primary.target.split('/')[0] || primary.target || '').trim();
      const payload = {
        githubConnections: cleaned,
        orgOrUsername: primaryOwner || null,
        personalAccessToken: primary.token,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      };
      await setDoc(integrationRef, payload, { merge: true });
      setGithubConnections(
        cleaned.map((row) => ({
          target: row.target,
          token: '',
          tokenConfigured: Boolean(row.token),
          tokenKind: detectGithubTokenKind(row.token),
        })),
      );
      setGithubConfigured(cleaned.some((r) => r.token));
      const targetLabels = cleaned
        .map((r) => r.target)
        .filter(Boolean)
        .join(', ');
      setGithubConfigMsg(
        targetLabels
          ? `Saved ${cleaned.length} GitHub connection(s) for: ${targetLabels}.`
          : `Saved ${cleaned.length} GitHub connection(s).`,
      );
    } catch (e) {
      console.error('save github config:', e);
      setGithubConfigError(e.message || 'Could not save GitHub configuration.');
    } finally {
      setGithubSaving(false);
    }
  };

  const handleRevokeGithubToken = async (index) => {
    if (!resolvedCompanyId) return;
    setGithubConfigError('');
    setGithubConfigMsg('');
    setGithubSaving(true);
    try {
      const integrationRef = doc(
        db,
        'companies',
        resolvedCompanyId,
        'integrations',
        GITHUB_INTEGRATION_DOC_ID,
      );
      const snap = await getDoc(integrationRef);
      const existing = snap.exists()
        ? extractGithubConnectionSecrets(snap.data() || {})
        : [];

      const nextSecrets = githubConnections.map((row, i) => {
        const target = typeof row.target === 'string' ? row.target.trim() : '';
        if (i === index) {
          return { target, token: '' };
        }
        if (row.tokenConfigured) {
          const byIndex = existing[i];
          const byTarget = existing.find(
            (s) => s.target && s.target === target && s.token,
          );
          return {
            target,
            token: (byIndex && byIndex.token) || (byTarget && byTarget.token) || '',
          };
        }
        return {
          target,
          token: typeof row.token === 'string' ? row.token.trim() : '',
        };
      });

      const withTokens = nextSecrets.filter((r) => r.token);
      const primary = withTokens[0] || nextSecrets[0] || { target: '', token: '' };
      const primaryOwner = (primary.target.split('/')[0] || primary.target || '').trim();

      await setDoc(
        integrationRef,
        {
          githubConnections: nextSecrets,
          orgOrUsername: primaryOwner || null,
          personalAccessToken: primary.token || null,
          updatedAt: serverTimestamp(),
          updatedByUid: auth.currentUser?.uid || null,
        },
        { merge: true },
      );

      setGithubConnections(
        nextSecrets.map((row) => ({
          target: row.target,
          token: '',
          tokenConfigured: Boolean(row.token),
          tokenKind: detectGithubTokenKind(row.token),
        })),
      );
      setGithubConfigured(nextSecrets.some((r) => r.token));
      setGithubConfigMsg(
        'Token revoked. Paste a classic PAT (ghp_…) and save to reconnect.',
      );
    } catch (e) {
      console.error('revoke github token:', e);
      setGithubConfigError(e.message || 'Could not revoke GitHub token.');
    } finally {
      setGithubSaving(false);
    }
  };

  const updateGithubConnection = (index, field, value) => {
    setGithubConnections((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
  };

  const addGithubConnection = () => {
    setGithubConnections((prev) => [...prev, emptyGithubConnection()]);
  };

  const removeGithubConnection = (index) => {
    setGithubConnections((prev) => {
      if (prev.length <= 1) return [emptyGithubConnection()];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSaveStabilityConfig = async () => {
    if (!resolvedCompanyId) return;
    const typed = stabilityKey.trim();

    setStabilityConfigError('');
    setStabilityConfigMsg('');

    const nextKey = isMaskedByokPlaceholder(typed, stabilityKeyStored)
      ? stabilityKeyStored.trim()
      : typed;

    if (!nextKey) {
      setStabilityConfigError('Enter your Stability API key.');
      return;
    }

    setStabilitySaving(true);
    try {
      const payload = {
        apiKey: nextKey,
        updatedAt: serverTimestamp(),
        updatedByUid: auth.currentUser?.uid || null,
      };
      await setDoc(
        doc(db, 'companies', resolvedCompanyId, 'integrations', STABILITY_INTEGRATION_DOC_ID),
        payload,
        { merge: true },
      );
      setStabilityKeyStored(nextKey);
      setStabilityKey(maskByokApiKey(nextKey));
      setStabilityConfigured(true);
      setStabilityConfigMsg('Stability AI API key saved.');
    } catch (e) {
      console.error('save stability config:', e);
      setStabilityConfigError(e.message || 'Could not save Stability configuration.');
    } finally {
      setStabilitySaving(false);
    }
  };

  if (!isCompanyAdmin) {
    return (
      <Alert severity="warning" sx={{ maxWidth: 560 }}>
        Workspace settings are available to Company Admins only.
      </Alert>
    );
  }

  if (!companyId && !userData?.companyId) {
    return (
      <Alert severity="info" sx={{ maxWidth: 560 }}>
        Your account is not linked to a company workspace yet.
      </Alert>
    );
  }

  if (workspaceLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const rawSection = searchParams.get('section');
  const activeSection = WORKSPACE_SECTIONS.some((s) => s.id === rawSection)
    ? rawSection
    : 'overview';
  const currentMeta = WORKSPACE_SECTIONS.find((s) => s.id === activeSection) || null;
  const goToSection = (id) => {
    if (!id || id === 'overview') {
      setSearchParams({}, { replace: false });
    } else {
      setSearchParams({ section: id }, { replace: false });
    }
  };

  const renderGeneral = () => <CompanyGeneralSettings userData={userData} />;

  const renderBranding = () => <WorkspaceBrandingSettings userData={userData} />;

  const renderRegional = () => <CompanyTimezoneSettings userData={userData} />;

  const renderAiRoster = () => (
    <AiWorkforceRosterPanel
      companyId={resolvedCompanyId || userData?.companyId || null}
      frictionSuggestionId={searchParams.get('frictionSuggestionId')}
      agentRoleHint={searchParams.get('agentRole')}
      onFrictionDeepLinkConsumed={() => {
        const next = new URLSearchParams(searchParams);
        next.delete('frictionSuggestionId');
        next.delete('agentRole');
        if (!next.get('section')) next.set('section', 'ai-roster');
        setSearchParams(next, { replace: true });
      }}
    />
  );

  const renderAiSpend = () => (
    <Stack spacing={3}>
      <AiSpendBudgetSettings
        companyId={resolvedCompanyId}
        userRole={userData?.role}
      />
      <AiSubscriptionsSettings
        companyId={resolvedCompanyId}
        userRole={userData?.role}
      />
    </Stack>
  );

  const renderModules = () => (
    <Stack spacing={2}>
      {requestError && (
        <Alert severity="error" onClose={() => setRequestError('')}>
          {requestError}
        </Alert>
      )}
      {requestMsg && (
        <Alert severity="success" onClose={() => setRequestMsg('')}>
          {requestMsg}
        </Alert>
      )}
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <ExtensionIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Active modules
            </Typography>
                <Chip
                  size="small"
                  label={`${enabledModules.length} of ${ACTIVE_MODULE_KEYS.length} enabled`}
                  color="primary"
                  variant="outlined"
                />
              </Stack>

              {enabledModules.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Enabled for your workspace
                  </Typography>
                  <List dense disablePadding sx={{ mb: 2 }}>
                    {enabledModules.map((key) => (
                      <ListItem key={key} disableGutters sx={{ py: 0.75 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={`${ACTIVE_MODULE_LABELS[key]} enabled`}
                          secondary={ACTIVE_MODULE_DESCRIPTIONS[key]}
                        />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {lockedModules.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    Available for upgrade
                  </Typography>
                  <Stack spacing={1.5}>
                    {lockedModules.map((key) => (
                      <Box
                        key={key}
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ xs: 'stretch', sm: 'center' }}
                          justifyContent="space-between"
                        >
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <LockOutlinedIcon color="disabled" sx={{ mt: 0.25 }} />
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {ACTIVE_MODULE_LABELS[key]}
                                <Chip
                                  size="small"
                                  label="Locked"
                                  sx={{ ml: 1 }}
                                  variant="outlined"
                                />
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {ACTIVE_MODULE_DESCRIPTIONS[key]}
                              </Typography>
                            </Box>
                          </Stack>
                          {pendingModuleKeys.has(key) ? (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled
                              sx={{
                                flexShrink: 0,
                                textTransform: 'none',
                                maxWidth: { xs: '100%', sm: 220 },
                                whiteSpace: 'normal',
                                textAlign: 'center',
                                py: 0.75,
                              }}
                            >
                              Request already in place{' '}
                              <Box
                                component="span"
                                sx={{ fontSize: '0.7rem', opacity: 0.8, display: 'inline' }}
                              >
                                (please wait until resolution)
                              </Box>
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                requestingKey === key ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <SendIcon />
                                )
                              }
                              onClick={() => handleRequestAccess(key)}
                              disabled={Boolean(requestingKey)}
                              sx={{ flexShrink: 0 }}
                            >
                              Request access
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </>
              )}
        </CardContent>
      </Card>
    </Stack>
  );

  const renderRoles = () => (
    <Stack spacing={3}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <GroupsIcon color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Role management
            </Typography>
          </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                When a module is enabled above, these internal roles can access it. Company Admins
                always manage the workspace; use Team to invite users and assign roles.
              </Typography>

              <Table size="small" sx={{ mb: 2 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Roles with access</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ACTIVE_MODULE_KEYS.map((key) => (
                    <TableRow key={key}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: modules[key] ? 600 : 400 }}>
                          {ACTIVE_MODULE_LABELS[key]}
                        </Typography>
                        {!modules[key] && (
                          <Typography variant="caption" color="text.secondary">
                            Not enabled
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" component="div">
                          {(MODULE_ROLE_ACCESS[key] || []).join(' · ')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Internal roles
              </Typography>
              <Stack spacing={1.5}>
                {INTERNAL_ROLES.map(({ role, summary }) => (
                  <Box key={role}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {role}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {summary}
                    </Typography>
                  </Box>
                ))}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SecurityIcon color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Granular Role Permissions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Control what Manager, Developer, and Agent roles can view or manage within
                  each active module. Company Admins always have full access.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="contained"
              startIcon={savingPermissions ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSavePermissions}
              disabled={!permissionsDirty || savingPermissions || activeMasterModuleKeys.length === 0}
            >
              Save Permissions
            </Button>
          </Stack>

          {permissionsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPermissionsError('')}>
              {permissionsError}
            </Alert>
          )}
          {permissionsMsg && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPermissionsMsg('')}>
              {permissionsMsg}
            </Alert>
          )}

          {activeMasterModuleKeys.length === 0 ? (
            <Alert severity="info">
              No modules are active yet. Ask your platform administrator to enable modules before
              configuring role permissions.
            </Alert>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>Role</TableCell>
                    {activeMasterModuleKeys.map((moduleKey) => (
                      <TableCell key={moduleKey} align="center" sx={{ fontWeight: 700, minWidth: 140 }}>
                        {MASTER_MODULE_LABELS[moduleKey]}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {PERMISSION_MANAGE_ROLES.map((role) => (
                    <TableRow key={role}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {PERMISSION_ROLE_LABELS[role]}
                        </Typography>
                      </TableCell>
                      {activeMasterModuleKeys.map((moduleKey) => (
                        <TableCell key={`${role}-${moduleKey}`} align="center">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                            flexWrap="wrap"
                            useFlexGap
                          >
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={hasRolePermission(rolePermissions, role, moduleKey, 'read')}
                                  onChange={(e) =>
                                    handlePermissionToggle(role, moduleKey, 'read', e.target.checked)
                                  }
                                />
                              )}
                              label="View"
                              sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                            />
                            <FormControlLabel
                              control={(
                                <Checkbox
                                  size="small"
                                  checked={hasRolePermission(rolePermissions, role, moduleKey, 'write')}
                                  onChange={(e) =>
                                    handlePermissionToggle(role, moduleKey, 'write', e.target.checked)
                                  }
                                />
                              )}
                              label="Manage"
                              sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.75rem' } }}
                            />
                          </Stack>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {isCompanyAdmin && (
        <Alert severity="info" sx={{ maxWidth: 720 }}>
          Team outreach and broadcast history have moved to{' '}
          <strong>Communications → Team outreach</strong> in the sidebar.
        </Alert>
      )}
    </Stack>
  );

  const renderIntegrations = () => (
    <Stack spacing={3}>
      {apiKeyError && (
        <Alert severity="error" onClose={() => setApiKeyError('')}>
          {apiKeyError}
        </Alert>
      )}
      {apiKeySuccess && (
        <Alert severity="success" onClose={() => setApiKeySuccess('')}>
          {apiKeySuccess}
        </Alert>
      )}
      {revealedKey && (
        <Alert
          severity="info"
          action={(
            <Button
              color="inherit"
              size="small"
              startIcon={<ContentCopyIcon />}
              onClick={() => handleCopyKey({ id: revealedKey.id, key: revealedKey.key })}
            >
              Copy full key
            </Button>
          )}
        >
          New key <strong>{revealedKey.name}</strong>:{' '}
          <Box component="code" sx={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
            {revealedKey.key}
          </Box>
        </Alert>
      )}

      <WebhookManager userData={userData} />

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <ContactsIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                CRM lead ingestion
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Webhook API key and endpoint for external CRM systems to push leads into your workspace.
              </Typography>
            </Box>
          </Stack>
          <CrmApiAccess />
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <AlternateEmailIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Outbound Email Provider (BYOK)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect your own SendGrid or Resend API key to send outbound email
                campaigns directly from ShiftyPM. Configure a dedicated provider per
                app, or a company master used as a fallback.
              </Typography>
            </Box>
          </Stack>

          {emailConfigError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEmailConfigError('')}>
              {emailConfigError}
            </Alert>
          )}
          {emailConfigMsg && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setEmailConfigMsg('')}>
              {emailConfigMsg}
            </Alert>
          )}

          {emailConfigLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ maxWidth: 520 }}>
              <TextField
                select
                label="Configuration applies to"
                value={emailConfigTarget}
                onChange={(e) => handleSelectEmailTarget(e.target.value)}
                fullWidth
                helperText="Pick an app for a dedicated provider, or the company master used as a fallback."
              >
                <MenuItem value={COMPANY_DEFAULT_CONFIG_ID}>
                  Company Master (Fallback)
                  {emailConfigs[COMPANY_DEFAULT_CONFIG_ID] ? ' • configured' : ''}
                </MenuItem>
                {crmApps.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.appName}
                    {emailConfigs[a.id] ? ' • configured' : ''}
                  </MenuItem>
                ))}
              </TextField>

              {emailConfigTarget !== COMPANY_DEFAULT_CONFIG_ID &&
                !emailConfigs[emailConfigTarget] && (
                  <Alert severity="info">
                    This app has no dedicated provider yet. Until you save one, its campaigns
                    fall back to the Company Master.
                  </Alert>
                )}

              {crmApps.length === 0 && (
                <Typography variant="caption" color="text.secondary">
                  Tip: add apps in Product Portfolio to give each its own dedicated provider.
                </Typography>
              )}

              <TextField
                select
                label="Provider"
                value={emailProvider}
                onChange={(e) => setEmailProvider(e.target.value)}
                fullWidth
              >
                {EMAIL_PROVIDERS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="API key"
                type={showEmailKey ? 'text' : 'password'}
                value={emailApiKey}
                onChange={(e) => setEmailApiKey(e.target.value)}
                fullWidth
                autoComplete="off"
                placeholder="Paste your provider API key"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowEmailKey((s) => !s)}
                        aria-label={showEmailKey ? 'Hide API key' : 'Show API key'}
                        edge="end"
                      >
                        {showEmailKey ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Verified sender email"
                type="email"
                value={emailSender}
                onChange={(e) => setEmailSender(e.target.value)}
                fullWidth
                placeholder="hello@yourcompany.com"
                helperText="Must be a verified sender/domain in your provider account."
              />

              <Box>
                <Button
                  variant="contained"
                  startIcon={
                    emailSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSaveEmailConfig}
                  disabled={emailSaving}
                >
                  Save Configuration
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <AccountTreeOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                GitHub Integration (GitOps)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Connect a GitHub organization or personal account so AI Game Studio projects
                can scaffold dedicated repositories for agent pull requests.
              </Typography>
            </Box>
          </Stack>

          {githubConfigError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGithubConfigError('')}>
              {githubConfigError}
            </Alert>
          )}
          {githubConfigMsg && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setGithubConfigMsg('')}>
              {githubConfigMsg}
            </Alert>
          )}

          {githubConfigLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ maxWidth: 720 }}>
              {(() => {
                const hasClassic = githubConnections.some(
                  (r) => r.tokenConfigured && r.tokenKind === 'classic',
                );
                const hasFineOnly =
                  githubConnections.some(
                    (r) => r.tokenConfigured && r.tokenKind === 'fine_grained',
                  ) && !hasClassic;
                if (hasFineOnly) {
                  return (
                    <Alert severity="warning">
                      No classic PAT (ghp_…) is saved. Game scaffolding needs a
                      classic owner-level token (e.g. target <code>tatotron</code>).
                      Fine-grained repo tokens cannot create new repositories.
                    </Alert>
                  );
                }
                if (hasClassic) {
                  return (
                    <Alert severity="info">
                      Classic PAT ready for scaffolding. Repo-specific
                      fine-grained tokens (if any) are only used for that
                      existing repo — they do not block creating new game repos.
                    </Alert>
                  );
                }
                return null;
              })()}

              <Typography variant="body2" color="text.secondary">
                Map a classic PAT (<code>ghp_…</code>) to a personal account
                login (e.g. <code>tatotron</code>) for creating new game repos.
                Optional: add a separate connection for an existing
                <code>owner/repo</code> if agents need access there. Scaffolding
                uses only this Workspace Settings data (not Firebase Secret Manager).
              </Typography>

              {githubConnections.map((row, index) => {
                const isRepoScoped =
                  typeof row.target === 'string' && row.target.includes('/');
                const fineGrainedRepoOk =
                  row.tokenConfigured &&
                  row.tokenKind === 'fine_grained' &&
                  isRepoScoped;
                const fineGrainedBlocking =
                  row.tokenConfigured &&
                  row.tokenKind === 'fine_grained' &&
                  !isRepoScoped;

                return (
                <Stack
                  key={`gh-conn-${index}`}
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ sm: 'flex-start' }}
                >
                  <TextField
                    label="Target (e.g., tatotron/shiftypm or just tatotron)"
                    value={row.target}
                    onChange={(e) =>
                      updateGithubConnection(index, 'target', e.target.value)
                    }
                    fullWidth
                    placeholder="tatotron/neon-blood or tatotron"
                    helperText={
                      index === 0
                        ? 'Owner-only (tatotron) for new repos; owner/repo for an existing repo.'
                        : ' '
                    }
                  />
                  <TextField
                    label="GitHub PAT"
                    type={row.tokenConfigured ? 'text' : showGithubPat ? 'text' : 'password'}
                    value={
                      row.tokenConfigured
                        ? row.tokenKind === 'fine_grained'
                          ? 'github_pat_************************'
                          : row.tokenKind === 'classic'
                            ? 'ghp_************************'
                            : '************************'
                        : row.token
                    }
                    onChange={(e) =>
                      updateGithubConnection(index, 'token', e.target.value)
                    }
                    disabled={row.tokenConfigured || githubSaving}
                    fullWidth
                    autoComplete="off"
                    placeholder="ghp_…"
                    helperText={
                      fineGrainedBlocking
                        ? 'Owner-level fine-grained tokens cannot create repos — revoke and use ghp_.'
                        : fineGrainedRepoOk
                          ? 'Fine-grained OK for this existing repo only. New game repos use a classic owner PAT.'
                          : row.tokenConfigured
                            ? 'Token saved. Revoke to paste a new PAT.'
                            : index === 0
                              ? 'Classic PAT (ghp_…) with repo scope for scaffolding.'
                              : ' '
                    }
                    error={fineGrainedBlocking}
                    InputProps={{
                      endAdornment:
                        !row.tokenConfigured && index === 0 ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => setShowGithubPat((s) => !s)}
                              aria-label={showGithubPat ? 'Hide PATs' : 'Show PATs'}
                              edge="end"
                            >
                              {showGithubPat ? (
                                <VisibilityOffIcon fontSize="small" />
                              ) : (
                                <VisibilityIcon fontSize="small" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ) : undefined,
                    }}
                  />
                  {row.tokenConfigured ? (
                    <Button
                      color="error"
                      variant="outlined"
                      disabled={githubSaving}
                      onClick={() => void handleRevokeGithubToken(index)}
                      sx={{ mt: { sm: 1 }, textTransform: 'none', flexShrink: 0 }}
                    >
                      Revoke
                    </Button>
                  ) : (
                    <IconButton
                      aria-label={`Remove GitHub connection ${index + 1}`}
                      onClick={() => removeGithubConnection(index)}
                      disabled={
                        githubConnections.length <= 1 && !row.target && !row.token
                      }
                      sx={{ mt: { sm: 1 }, alignSelf: { xs: 'flex-end', sm: 'flex-start' } }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  )}
                </Stack>
                );
              })}

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addGithubConnection}
                  disabled={githubSaving}
                >
                  Add Another Connection
                </Button>
                <Button
                  variant="contained"
                  startIcon={
                    githubSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSaveGithubConfig}
                  disabled={githubSaving}
                >
                  Save GitHub Configuration
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <MultiProviderConfig companyId={resolvedCompanyId} />

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <ImageOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Stability AI
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Used for the Game Studio Asset Generator (SD 3.5 &amp; Background Removal).
              </Typography>
            </Box>
          </Stack>

          {stabilityConfigError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStabilityConfigError('')}>
              {stabilityConfigError}
            </Alert>
          )}
          {stabilityConfigMsg && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStabilityConfigMsg('')}>
              {stabilityConfigMsg}
            </Alert>
          )}

          {stabilityConfigLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ maxWidth: 520 }}>
              {stabilityConfigured && (
                <Alert severity="info">
                  Stability AI BYOK is configured for this workspace.
                </Alert>
              )}

              <TextField
                label="Stability API Key"
                type={showStabilityKey ? 'text' : 'password'}
                value={stabilityKey}
                onChange={(e) => setStabilityKey(e.target.value)}
                onFocus={() => {
                  if (isMaskedByokPlaceholder(stabilityKey, stabilityKeyStored)) {
                    setStabilityKey('');
                  }
                }}
                fullWidth
                autoComplete="off"
                placeholder="sk-…"
                helperText="Paste a Stability AI API key. Existing keys are shown masked until you replace them."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowStabilityKey((s) => !s)}
                        aria-label={
                          showStabilityKey ? 'Hide Stability API key' : 'Show Stability API key'
                        }
                        edge="end"
                      >
                        {showStabilityKey ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box>
                <Button
                  variant="contained"
                  startIcon={
                    stabilitySaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSaveStabilityConfig}
                  disabled={stabilitySaving}
                >
                  Save Stability Configuration
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <StorageOutlinedIcon color="primary" />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                File Storage Warehouse (BYOB)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose where chat attachments and uploaded files are stored for your workspace.
                External providers require bucket/folder details and credentials.
              </Typography>
            </Box>
          </Stack>

          {storageConfigError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStorageConfigError('')}>
              {storageConfigError}
            </Alert>
          )}
          {storageConfigMsg && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setStorageConfigMsg('')}>
              {storageConfigMsg}
            </Alert>
          )}

          {storageConfigLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Stack spacing={2} sx={{ maxWidth: 520 }}>
              <TextField
                select
                label="Storage provider"
                value={storageProvider}
                onChange={(e) => setStorageProvider(e.target.value)}
                fullWidth
              >
                {STORAGE_PROVIDERS.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>

              {storageProvider !== 'shiftypm_default' && (
                <TextField
                  label="Bucket name / Folder ID"
                  value={storageBucket}
                  onChange={(e) => setStorageBucket(e.target.value)}
                  fullWidth
                  placeholder={
                    storageProvider === 'aws_s3'
                      ? 'my-company-chat-uploads'
                      : '1a2b3c4d5e6f7g8h9i0j'
                  }
                  helperText={
                    storageProvider === 'aws_s3'
                      ? 'S3 bucket where chat attachments will be stored.'
                      : 'Google Drive folder ID for service-account uploads.'
                  }
                />
              )}

              {storageProvider !== 'shiftypm_default' && (
                <TextField
                  label="Credentials JSON / Secret Key"
                  type={showStorageCredentials ? 'text' : 'password'}
                  value={storageCredentials}
                  onChange={(e) => setStorageCredentials(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                  autoComplete="off"
                  placeholder={
                    storageProvider === 'aws_s3'
                      ? 'AKIA... or IAM secret access key'
                      : '{ "type": "service_account", ... }'
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() => setShowStorageCredentials((s) => !s)}
                          aria-label={
                            showStorageCredentials ? 'Hide credentials' : 'Show credentials'
                          }
                          edge="end"
                        >
                          {showStorageCredentials ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  helperText="Stored in Firestore for the MVP; a future engine will move secrets to a vault."
                />
              )}

              <Box>
                <Button
                  variant="contained"
                  startIcon={
                    storageSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SaveIcon />
                    )
                  }
                  onClick={handleSaveStorageConfig}
                  disabled={storageSaving}
                >
                  Save Configuration
                </Button>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2 }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <VpnKeyIcon color="primary" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  API Keys &amp; Integrations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Connect external apps, websites, and IoT devices to your workspace.
                </Typography>
              </Box>
            </Stack>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openGenerateDialog}>
              Generate New API Key
            </Button>
          </Stack>

          {apiKeysLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : activeApiKeys.length === 0 ? (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No active API keys yet. Generate one to integrate an external system.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Key</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeApiKeys.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.name || 'Unnamed'}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          component="code"
                          sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                        >
                          {maskApiKey(row.key)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatApiKeyDate(row.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title={copiedId === row.id ? 'Copied!' : 'Copy key'}>
                            <IconButton size="small" onClick={() => handleCopyKey(row)}>
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Revoke key">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={revokingId === row.id}
                                onClick={() => handleRevokeKey(row)}
                              >
                                {revokingId === row.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <BlockIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={generateOpen} onClose={generating ? undefined : () => setGenerateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Generate new API key</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            label="Key name"
            placeholder="e.g. Main Website, IoT Device"
            value={keyName}
            onChange={(e) => {
              setKeyName(e.target.value);
              setKeyNameError('');
            }}
            error={Boolean(keyNameError)}
            helperText={keyNameError || 'A label to identify where this key is used'}
            disabled={generating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)} disabled={generating}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerateApiKey} disabled={generating}>
            {generating ? 'Generating…' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );

  const sectionRenderers = {
    general: renderGeneral,
    branding: renderBranding,
    modules: renderModules,
    roles: renderRoles,
    integrations: renderIntegrations,
    'ai-spend': renderAiSpend,
    'ai-roster': renderAiRoster,
    regional: renderRegional,
  };

  return (
    <DashboardModuleRoot>
      <DashboardModuleToolbar sx={{ justifyContent: 'flex-start' }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="workspace settings breadcrumb"
          sx={{ flex: 1, minWidth: 0 }}
        >
          {activeSection === 'overview' ? (
            <Typography color="text.primary" sx={{ fontWeight: 600 }}>
              Workspace
            </Typography>
          ) : (
            <Link
              component="button"
              type="button"
              underline="hover"
              color="inherit"
              onClick={() => goToSection('overview')}
            >
              Workspace
            </Link>
          )}
          {currentMeta && (
            <Typography color="text.primary" sx={{ fontWeight: 600 }}>
              {currentMeta.label}
            </Typography>
          )}
        </Breadcrumbs>
        {activeSection !== 'overview' && (
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => goToSection('overview')}
            sx={{ textTransform: 'none', flexShrink: 0 }}
          >
            All settings
          </Button>
        )}
      </DashboardModuleToolbar>

      <DashboardModuleBody sx={{ px: { xs: 1.5, sm: 2.5 }, py: 2 }}>
      {activeSection === 'overview' ? (
        <>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Workspace settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your organization&apos;s Business App Hub for{' '}
            <strong>{workspace?.name || 'your company'}</strong>. Choose a section to manage.
          </Typography>

          <Grid container spacing={2}>
            {WORKSPACE_SECTIONS.map((s) => {
              const Icon = s.icon;
              return (
                <Grid key={s.id} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <Card variant="outlined" sx={{ height: '100%' }}>
                    <CardActionArea
                      onClick={() => goToSection(s.id)}
                      sx={{ height: '100%', p: 0.5 }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          justifyContent="space-between"
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                display: 'flex',
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: 'action.hover',
                                color: 'primary.main',
                              }}
                            >
                              <Icon />
                            </Box>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {s.label}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {s.description}
                              </Typography>
                            </Box>
                          </Stack>
                          <ChevronRightIcon color="action" />
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 4, mb: 1.5 }}>
            More settings
          </Typography>
          <Grid container spacing={2}>
            {RELATED_SETTINGS.map((s) => {
              const Icon = s.icon;
              return (
                <Grid key={s.tab} size={{ xs: 12, sm: 6, lg: 4 }}>
                  <Card variant="outlined" sx={{ height: '100%', opacity: s.disabled ? 0.6 : 1 }}>
                    <CardActionArea
                      onClick={() => navigate(dashboardPathForTab(s.tab))}
                      disabled={s.disabled}
                      sx={{ height: '100%', p: 0.5 }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          justifyContent="space-between"
                        >
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box
                              sx={{
                                display: 'flex',
                                p: 1,
                                borderRadius: 1.5,
                                bgcolor: 'action.hover',
                                color: 'text.secondary',
                              }}
                            >
                              <Icon />
                            </Box>
                            <Box>
                              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                {s.label}
                                {s.hint && (
                                  <Chip size="small" label={s.hint} sx={{ ml: 1 }} variant="outlined" />
                                )}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {s.description}
                              </Typography>
                            </Box>
                          </Stack>
                          {!s.disabled && <ChevronRightIcon color="action" />}
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : (
        <>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            {currentMeta?.label}
          </Typography>
          {currentMeta?.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {currentMeta.description}
            </Typography>
          )}
          {sectionRenderers[activeSection]?.()}
        </>
      )}
      </DashboardModuleBody>
    </DashboardModuleRoot>
  );
}
